import PlatformCapability from "../PlatformCapability";
import DecisionTrailPreview from "../DecisionTrailPreview";
import DesignAuthorityTrailPreview from "../DesignAuthorityTrailPreview";
import { ImageWithFallback } from "../utils/ImageWithFallback";

const PHASE_ORDER = ['Shape', 'Decide', 'Build', 'Check', 'Learn'];

const features = [
  {
    "id": 1,
    "phase": "Shape",
    "title": "Keep your strategy alive as you build.",
    "description": "Use",
    "capability": {
      "name": "Product Strategy",
      "doc": "product-strategy",
      "kind": "feature"
    },
    "outcome": "to guide each new build. As you learn, Pabs proposes updates so your team and AI tools can work from the latest agreed direction.",
    "image": "/images/homepage/pabs-strategy-generator.jpg"
  },
  {
    "id": 2,
    "phase": "Learn",
    "title": "Know what needs your attention each week.",
    "description": "Set a Monday check-in with",
    "capability": {
      "name": "schedule_pulse",
      "doc": "pulse",
      "kind": "tool"
    },
    "outcome": "for a Pulse on what moved, what stalled and which decisions need you. Start the week knowing what matters to your goal.",
    "image": "/images/homepage/pabs-strategy-pulse.svg"
  },
  {
    "id": 3,
    "phase": "Decide",
    "title": "Follow your decision through to what changed.",
    "description": "When someone questions a shipped change, open",
    "capability": {
      "name": "Decisions",
      "doc": "decisions",
      "kind": "feature"
    },
    "outcome": "to trace the reason, linked work and returned evidence together. See what was agreed, what actually changed and whether the expected result is still unproven.",
    "relatedCapabilities": [
      {"name": "Product Decisions", "doc": "decisions", "kind": "feature"},
      {"name": "Competitor Analysis", "doc": "competitor-analysis", "kind": "feature"}
    ],
    "preview": "decision-trail"
  },
  {
    "id": 4,
    "phase": "Build",
    "title": "Keep your agents working toward the goal.",
    "description": "When your agent proposes a social feed, the installed Pabs skill guides it to check the idea against your goal. It reads relevant context through MCP, using",
    "capability": {
      "name": "get_full_context",
      "doc": "integrations-overview",
      "kind": "tool"
    },
    "outcome": "when it needs the full picture. Changes to the agreed scope come back to you.",
    "image": "/images/homepage/pabs-mcp-advisor.jpg"
  },
  {
    "id": 5,
    "phase": "Check",
    "title": "Let Pabs coordinate the review and the fix.",
    "description": "With",
    "capability": {
      "name": "Design Authority",
      "doc": "design-authority",
      "kind": "feature"
    },
    "outcome": "enabled, Pabs reviews your builder’s work, prepares focused corrections and checks the returned version. Your connected agent picks up the work through MCP. You make the decisions about scope and whether the work can move forward.",
    "preview": "design-authority-trail"
  },
  {
    "id": 6,
    "phase": "Learn",
    "title": "Let customer evidence challenge the plan.",
    "description": "Customers keep struggling with a flow you thought was solved. The",
    "capability": {
      "name": "Learnings Engine",
      "doc": "learnings-how-it-works",
      "kind": "feature"
    },
    "outcome": "automatically checks captured observations against your assumptions and decisions, then proposes specific strategy updates. Review the changes; once approved, they guide your team and connected agents.",
    "image": "/images/homepage/pabs-evidence-roadmaps.jpg"
  },
].sort((a, b) => PHASE_ORDER.indexOf(a.phase) - PHASE_ORDER.indexOf(b.phase));

// Keep the feature story with its phase, instead of a separate scroll track.
export default function PlatformFeatures({phase}) {
  const phaseFeatures = features.filter(feature => !phase || feature.phase.toLowerCase() === phase);
  return <div className="platform-phase-features">
    {phaseFeatures.map(feature => <article key={feature.id} data-feature-id={feature.id} className={`platform-phase-feature${!feature.image && !feature.preview ? " platform-phase-feature-text" : ""}`}>
      <div className="platform-phase-feature-copy">
        <h3>{feature.title}</h3>
        {(feature.image || feature.preview) && <p>{feature.description} <PlatformCapability {...feature.capability}/> {feature.outcome}</p>}
        {feature.relatedCapabilities && <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm">
          {feature.relatedCapabilities.map(capability => <PlatformCapability key={capability.doc} {...capability}/>)}
        </div>}
      </div>
      {feature.image || feature.preview ? <div className={`platform-phase-feature-image${feature.preview ? " platform-phase-feature-preview" : ""}`}>
        {feature.preview === "decision-trail" ? <DecisionTrailPreview label={`Illustrative example: ${feature.title}`}/> : feature.preview === "design-authority-trail" ? <DesignAuthorityTrailPreview label={`Illustrative example: ${feature.title}`}/> : <ImageWithFallback src={feature.image} alt={feature.title} loading="lazy" className="w-full h-full object-contain"/>}
      </div> : <p className="platform-phase-feature-detail">{feature.description} <PlatformCapability {...feature.capability}/> {feature.outcome}</p>}
    </article>)}
  </div>;
}
